import { BankOutlined, CrownOutlined, EditOutlined } from '@ant-design/icons'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { Button, Card, Descriptions, Flex, List, Tag, Tooltip, Typography } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth'
import { ColleagueTable } from '../user/components/colleague-table'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { NetworkErrors } from '../components/network-errors'
import { PageHeader } from '../components/page-header'
import { useTitle } from '../hooks/use-title'
import { useCompany, useCompanyUsage, useCompanyUsers, useStartCompanyTrial } from '../company/hooks'
import { CompanyUpdateModal } from '../company/components/company-update-modal'
import { AgencySettingsModal } from '../company/components/agency-settings-modal'
import {
    INDEX_COMPANY_USER,
    MANAGE_AGENCY,
    SHOW_COMPANY,
    START_COMPANY_TRIAL,
    UPDATE_COMPANY,
    SHOW_COMPANY_USAGE,
} from '../user/permission'
import { UsageProgress } from '../components/usage-progress'

export const Route = createFileRoute('/_auth/_main/companies/$companyId/')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(SHOW_COMPANY)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: ShowCompany,
})

function ShowCompany() {
    const { t } = useTranslation()
    const { companyId } = Route.useParams()
    const { can } = useAuth()
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
    const [isAgencyModalOpen, setIsAgencyModalOpen] = useState(false)
    const query = useCompany(companyId)
    const canViewColleagues = can(INDEX_COMPANY_USER)
    const canViewCompanyUsage = can(SHOW_COMPANY_USAGE)
    const canStartCompanyTrial = can(START_COMPANY_TRIAL)
    const canUpdateCompany = can(UPDATE_COMPANY)
    const canManageAgency = can(MANAGE_AGENCY)
    const colleaguesQuery = useCompanyUsers(canViewColleagues ? companyId : undefined)
    const startTrialMutation = useStartCompanyTrial(companyId)
    const usageQuery = useCompanyUsage(canViewCompanyUsage ? companyId : '')

    useTitle(t('Company'))

    if (!query.isSuccess) {
        return (
            <NetworkErrors
                query={query}
                result404={{
                    title: t('Company not found'),
                }}
            />
        )
    }

    const company = query.data
    const hasSubscriptions = (company.subscriptions?.length || 0) > 0
    const isOnTrial = company.trialEndsAt ? company.trialEndsAt > new Date() : false
    const hasBillingDetails = !!company.name && !!company.address && !!company.city && !!company.postalCode
    const agencyButtonDisabled = !company.isAgency && !hasBillingDetails

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/companies">{t('Companies')}</Link>,
                    },
                    {
                        title: company.name,
                    },
                ]}
            />
            <PageHeader
                prefix={
                    <Flex
                        align="center"
                        justify="center"
                        className="w-8 h-8 rounded-full bg-gray-100"
                    >
                        <BankOutlined />
                    </Flex>
                }
                title={
                    <div className="flex items-center gap-4">
                        {company.name}
                        {company.isAgency && (
                            <Tag
                                color="gold"
                                icon={<CrownOutlined />}
                                className="m-0"
                            >
                                {t('Agency')}
                            </Tag>
                        )}
                    </div>
                }
            >
                <Flex
                    gap="small"
                    align="center"
                >
                    {canManageAgency && (
                        <Tooltip
                            title={
                                agencyButtonDisabled
                                    ? t('Fill in company billing details before enabling agency')
                                    : undefined
                            }
                        >
                            <span>
                                <Button
                                    icon={<CrownOutlined />}
                                    disabled={agencyButtonDisabled}
                                    onClick={() => setIsAgencyModalOpen(true)}
                                >
                                    {t('Agency tier')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {canUpdateCompany && (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsUpdateModalOpen(true)}
                        >
                            {t('Edit')}
                        </Button>
                    )}
                </Flex>
            </PageHeader>

            <Flex
                vertical
                gap="middle"
                className="py-6"
            >
                <Flex
                    gap="middle"
                    className="flex-col lg:flex-row"
                >
                    <Descriptions
                        bordered
                        className="min-w-120"
                        size="small"
                        column={1}
                        items={[
                            {
                                key: 'email',
                                label: t('Email'),
                                children: company.email || '-',
                            },
                            {
                                key: 'address',
                                label: t('Address'),
                                children: company.address || '-',
                            },
                            {
                                key: 'city',
                                label: t('City'),
                                children: company.city || '-',
                            },
                            {
                                key: 'postalCode',
                                label: t('Postal code'),
                                children: company.postalCode || '-',
                            },
                            {
                                key: 'vatNumber',
                                label: t('VAT Number'),
                                children: company.vatNumber || '-',
                            },
                        ]}
                    />
                    <Card className="shadow-xs w-full">
                        <Flex
                            align="center"
                            justify="space-between"
                            className="mb-4"
                        >
                            <Typography.Title
                                level={5}
                                className="mb-0"
                            >
                                {t('Subscriptions')}
                            </Typography.Title>
                            {!hasSubscriptions && canStartCompanyTrial && (
                                <Button
                                    type="primary"
                                    disabled={
                                        !query.data.name ||
                                        !query.data.address ||
                                        !query.data.city ||
                                        !query.data.postalCode
                                    }
                                    loading={startTrialMutation.isPending}
                                    onClick={() => startTrialMutation.mutate()}
                                >
                                    {t('Start trial without card')}
                                </Button>
                            )}
                            {isOnTrial && (
                                <Typography.Text
                                    type="secondary"
                                    className="block"
                                >
                                    {t('Trial ends: {{date}}', {
                                        date: company.trialEndsAt?.toLocaleDateString(),
                                    })}
                                </Typography.Text>
                            )}
                        </Flex>
                        <Flex
                            vertical
                            gap="large"
                        >
                            <List
                                locale={{ emptyText: t('No subscriptions') }}
                                dataSource={company.subscriptions || []}
                                renderItem={(subscription) => (
                                    <List.Item>
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className="w-full"
                                        >
                                            <Typography.Text>
                                                {subscription.tier && subscription.period
                                                    ? `${t(`tier.${subscription.tier}`)} (${t(subscription.period)})`
                                                    : subscription.priceId || subscription.type}
                                            </Typography.Text>
                                            <Flex
                                                align="center"
                                                gap="small"
                                            >
                                                <Tag color={subscription.active ? 'green' : 'default'}>
                                                    {subscription.active ? t('Active') : t('Inactive')}
                                                </Tag>
                                                <Typography.Text type="secondary">
                                                    {subscription.endsAt
                                                        ? t('Ends: {{date}}', {
                                                              date: subscription.endsAt.toLocaleDateString(),
                                                          })
                                                        : t('No end date')}
                                                </Typography.Text>
                                            </Flex>
                                        </Flex>
                                    </List.Item>
                                )}
                            />
                            {canViewCompanyUsage && query.data.tier && (
                                <UsageProgress
                                    usage={usageQuery.data?.usage}
                                    limit={usageQuery.data?.limit}
                                    overageUsage={usageQuery.data?.overageUsage}
                                    overageUnitAmount={usageQuery.data?.overageUnitAmount}
                                    trial={isOnTrial}
                                />
                            )}
                        </Flex>
                    </Card>
                </Flex>
                {canViewColleagues && (
                    <Card className="shadow-xs w-full">
                        <Typography.Title
                            level={5}
                            className="mb-4"
                        >
                            {t('Colleagues')}
                        </Typography.Title>
                        <ColleagueTable
                            rowKey="userId"
                            pagination={false}
                            loading={colleaguesQuery.isPending}
                            dataSource={colleaguesQuery.data}
                        />
                    </Card>
                )}
            </Flex>
            {canUpdateCompany && (
                <CompanyUpdateModal
                    company={company}
                    open={isUpdateModalOpen}
                    onCancel={() => setIsUpdateModalOpen(false)}
                />
            )}
            {canManageAgency && (
                <AgencySettingsModal
                    company={company}
                    open={isAgencyModalOpen}
                    onCancel={() => setIsAgencyModalOpen(false)}
                />
            )}
        </>
    )
}
